import type { PermissionModule } from "@core/session/types";
import type { Permission } from "@core/types";

// MODULOS_PERM (admin.jsx:3322) + Contenido (ANNEX-010 §2.1, RF-SEG-13
// modificado): siete módulos, orden exacto de SPEC-216 §4. El prototipo
// (variante e3e6245dff7d, agosto 2026) no incluye "Contenido" todavía —
// admin.jsx quedó desactualizado respecto al anexo, así que este orden sigue
// la SPEC, no el prototipo, para ese único módulo agregado.
export const PERMISSION_MODULES: {
  module: PermissionModule;
  label: string;
}[] = [
  { module: "members", label: "Miembros" },
  { module: "prizes", label: "Premios" },
  { module: "points_engine", label: "Motor de Puntos" },
  { module: "catalogs", label: "Catálogos" },
  { module: "content", label: "Contenido" },
  { module: "security", label: "Seguridad" },
  { module: "qr_validator", label: "Validador QR" },
];

// Los seis módulos del portal, es decir todos salvo Validador QR — los que
// SPEC-216 §4.1 bloquea cuando el perfil es de comercio aliado.
export const PORTAL_MODULES: PermissionModule[] = PERMISSION_MODULES.filter(
  (item) => item.module !== "qr_validator",
).map((item) => item.module);

// ACCIONES_PERM (admin.jsx:3323).
export const PERMISSION_ACTIONS: {
  action: "canView" | "canCreate" | "canEdit" | "canInactivate";
  label: string;
}[] = [
  { action: "canView", label: "Ver" },
  { action: "canCreate", label: "Crear" },
  { action: "canEdit", label: "Editar" },
  { action: "canInactivate", label: "Inactivar" },
];

// UUID sembrado del perfil Administrador (api/src/core/security/staff-users/
// staff-users.types.ts:126 y api/src/core/authorization/authorization.types.ts:29,
// misma constante en ambos). El contrato (`Profile`) no expone un flag
// explícito tipo `isAdministrator`: solo `isSystem`, común a los cuatro
// perfiles semilla. Para bloquear en la interfaz los cuatro checkboxes de
// Seguridad únicamente en el perfil Administrador (SPEC-216 §5) — no en
// Operador/Asesor/Comercio, que también son `isSystem` — no hay otra señal
// disponible que este id fijo, así que se copia literal del backend.
// **Incertidumbre real, no una decisión de negocio de este escritor**: si
// alguna vez ese id deja de ser estable entre entornos, este bloqueo de UI
// se rompe silenciosamente (seguiría funcionando la validación real del
// servidor, que si usa el mismo id). Reportado en el run-step para que
// discuss-spec decida si el contrato debe exponer un flag explícito.
export const ADMINISTRATOR_PROFILE_ID = "00000000-0000-0000-0000-000000000001";

function emptyPermission(module: PermissionModule): Permission {
  return {
    module,
    canView: false,
    canCreate: false,
    canEdit: false,
    canInactivate: false,
  };
}

export function emptyMatrix(): Permission[] {
  return PERMISSION_MODULES.map((item) => emptyPermission(item.module));
}

// Completa los siete módulos aunque el perfil (semilla o de otra
// integración) traiga menos filas — nunca se asume que el backend manda las
// siete.
export function matrixFromProfile(permissions: Permission[]): Permission[] {
  return PERMISSION_MODULES.map(
    (item) =>
      permissions.find((permission) => permission.module === item.module) ??
      emptyPermission(item.module),
  );
}

export function fullAccess(permission: Permission): boolean {
  return (
    permission.canView &&
    permission.canCreate &&
    permission.canEdit &&
    permission.canInactivate
  );
}

// Reglas de coherencia de SPEC-216 §4 (el backend las reaplica igual — esto
// es solo feedback inmediato en cliente, ver web/AGENTS.md):
// - Sin ver no hay nada: desmarcar ver desmarca las otras tres.
// - Marcar crear, editar o inactivar marca ver automáticamente.
export function toggleAction(
  permission: Permission,
  action: "canView" | "canCreate" | "canEdit" | "canInactivate",
): Permission {
  if (action === "canView") {
    if (permission.canView) {
      return emptyPermission(permission.module);
    }
    return { ...permission, canView: true };
  }
  const turningOn = !permission[action];
  return {
    ...permission,
    [action]: turningOn,
    canView: turningOn ? true : permission.canView,
  };
}

export function toggleMatrixAction(
  matrix: Permission[],
  module: PermissionModule,
  action: "canView" | "canCreate" | "canEdit" | "canInactivate",
): Permission[] {
  return matrix.map((permission) =>
    permission.module === module
      ? toggleAction(permission, action)
      : permission,
  );
}

// SPEC-216 §4.1: al marcar un perfil como de comercio aliado, los seis
// módulos del portal quedan desactivados y bloqueados; si ya tenían permisos
// marcados, se limpian al mismo tiempo. Validador QR no se toca.
export function clearPortalModules(matrix: Permission[]): Permission[] {
  return matrix.map((permission) =>
    PORTAL_MODULES.includes(permission.module)
      ? emptyPermission(permission.module)
      : permission,
  );
}

// SPEC-216 §5: el perfil Administrador conserva siempre acceso total a
// Seguridad — cualquier intento de retirárselo se rechaza en el servidor
// (`ADMIN_SECURITY`, authorization.rules.ts). Este helper solo fuerza el
// mismo resultado en la UI para que el checkbox nunca se muestre destildado
// mientras se edita.
export function withAdministratorSecurityLocked(
  matrix: Permission[],
): Permission[] {
  return matrix.map((permission) =>
    permission.module === "security"
      ? {
          module: permission.module,
          canView: true,
          canCreate: true,
          canEdit: true,
          canInactivate: true,
        }
      : permission,
  );
}
